import {Map, Record, OrderedSet} from 'immutable'
import fetchJSON from 'lib/fetchJSON'
import _ from 'lodash'
import {pending, fulfilled, rejected} from 'lib/promiseMiddlewareHelpers'
import {buildQueryString} from 'lib/utils'
import {deserializePhoto} from 'lib/api/records'

// Actions
export const LOAD_INDEX_TOPIC = 'rideReports/LOAD_INDEX_TOPIC'
export const NEW_REPORT = 'rideReports/NEW_REPORT'
export const UPDATE_REPORT = 'rideReports/UPDATE_REPORT'
export const LOAD_REPORT = 'rideReports/LOAD_REPORT'
export const LOAD_REPORTS = 'rideReports/LOAD_REPORTS'
export const SAVE_REPORT = 'rideReports/SAVE_REPORT'
export const DELETE_REPORT = 'rideReports/DELETE_REPORT'
export const LIKE_REPORT = 'rideReports/LIKE_REPORT'
export const UNLIKE_REPORT = 'rideReports/UNLIKE_REPORT'
export const QUEUE_SAVE = 'rideReports/QUEUE_SAVE'
export const ADD_PHOTO = 'rideReports/ADD_PHOTO'
export const ASSOCIATE_PHOTO = 'rideReports/ASSOCIATE_PHOTO'
export const DELETE_PHOTO = 'rideReports/DELETE_PHOTO'
export const RESTORE_REPORT = 'rideReports/RESTORE_REPORT'

// rails publish status conversion helpers
export const STATUS_DRAFT = 0
export const STATUS_FRIENDS = 1
export const STATUS_EVERYONE = 2

const statusFromRails = (railsStatusCode) => (
  {
    '0': 'draft',
    '1': 'friends',
    '2': 'everyone'
  }[railsStatusCode]
)

const statusToRails = (friendlyStatus) => (
  {
    'draft': 0,
    'friends': 1,
    'everyone': 2
  }[friendlyStatus]
)

const moderationFromRails = (railsStatusCode) => (
  isFinite(railsStatusCode) && {
    '0': 'visible',
    '1': 'featured',
    '2': 'suppressed'
  }[railsStatusCode]
)

const moderationToRails = (railsStatusCode) => (
  {
    'visible': 0,
    'featured': 1,
    'suppressed': 2
  }[railsStatusCode]
)

// Model
export const initialState = Record({
  reports: Map(), // actual report objects, in pure JSON form
  topics: Map(), // categories, like 'featured', 'recent', 'friends'
  users: Map(), // users' feeds, used in profile pages
  reportComments: Map() // key=reportId, value=OrderedMap<commentId, commentObj>
})()

export const TopicRecord = Record({
  loading: false,
  error: false,
  reportIds: OrderedSet(),
  nextPage: 0,
  resultsCount: null
})

export const deserializeReport = (fromRails) => {
  const reportContent = fromRails.report_content ? JSON.parse(fromRails.report_content) : null
  const publishStatus = statusFromRails(fromRails.publish_status.toString())

  return {
    id: fromRails.id,
    slug: fromRails.slug,
    title: fromRails.title,
    subtitle: fromRails.subtitle,
    publishStatus,
    reportContent,
    rides: fromRails.ride_ids, // TODO update references to this and rename to rideIds
    user: fromRails.user,
    metricUnits: fromRails.metric_units,
    photos: fromRails.photos.map(deserializePhoto),
    likedBy: fromRails.liked_by,
    likedByCurrentUser: fromRails.liked_by_current_user,
    likesCount: fromRails.likes_count,
    viewCount: fromRails.view_count,
    createdAt: fromRails.created_at,
    updatedAt: fromRails.updated_at,
    publishedAt: fromRails.published_at,
    deletedAt: fromRails.deleted_at,
    contestEntry: !!fromRails.contest_entry,
    featuredImage: fromRails.featured_image_id,
    queuedSave: false,
    moderationStatus: moderationFromRails(fromRails.moderation_status)
  }
}

export const serializeReport = (report) => {
  const fieldsToSerialize = ['title', 'subtitle', 'rides']

  const railsObj = {
    ..._.pick(report, fieldsToSerialize),
    report_content: JSON.stringify(report.reportContent),
    publish_status: statusToRails(report.publishStatus),
    metric_units: report.metricUnits,
    contest_entry: report.contestEntry,
    featured_image_id: report.featuredImage,
    updated_at: report.updatedAt
  }

  if (report.moderationStatus) {
    railsObj.moderation_status = moderationToRails(report.moderationStatus)
  }

  return JSON.stringify(railsObj)
}

// Reducer
export const reducer = (state = initialState, action = {}) => {
  switch (action.type) {
    case pending(LOAD_INDEX_TOPIC): {
      const {endpoint} = action.meta
      if (!state.topics.get(endpoint)) {
        const topics = state.topics.set(endpoint, new TopicRecord({loading: true}))
        return state.merge({topics})
      }
      return state.mergeDeep({topics: {[endpoint]: {loading: true}}})
    }
    case fulfilled(LOAD_INDEX_TOPIC): {
      const {endpoint, page} = action.meta
      // this line is just to guard against the (weird, should never happen)
      // case where the fulfilled action gets dispatched before the pending
      // action
      const topic = state.topics.get(endpoint) || new TopicRecord()

      const reports = action.payload.results
      const reportIds = topic.reportIds.concat(reports.map(r => r.id))
      const resultsCount = action.payload.results_count
      const nextPage = resultsCount > reportIds.size ? page + 1 : -1

      const withTopicUpdate = state.setIn(['topics', endpoint],
        topic.merge({
          loading: false,
          error: false,
          reportIds,
          nextPage,
          resultsCount
        })
      )

      return withTopicUpdate.merge({
        reports: state.reports.merge(Map(reports.map(r => [r.id, deserializeReport(r)])))
      })
    }
    case rejected(LOAD_INDEX_TOPIC): {
      const {endpoint} = action.meta
      const topic = state.topics.get(endpoint) || new TopicRecord()
      return state.mergeDeep({
        topics: {[endpoint]: topic.merge({loading: false, error: true})}
      })
    }
    case fulfilled(NEW_REPORT): {
      // TODO check action.payload.errors here?
      const report = deserializeReport(action.payload.report)
      return state.merge({reports: state.reports.set(report.id, report)})
    }
    case fulfilled(LOAD_REPORT): {
      const report = deserializeReport(action.payload)
      return state.merge({reports: state.reports.set(report.id, report)})
    }
    case pending(LOAD_REPORTS): {
      const {topicName} = action.meta
      if (topicName) {
        const topic = state.topics.get(topicName) || new TopicRecord()
        return state.setIn(['topics', topicName], topic.merge({loading: true}))
      }
      return state
    }
    case fulfilled(LOAD_REPORTS): {
      const reports = Map(action.payload.results.map(deserializeReport).map(r => [r.id, r]))
      const {topicName, page} = action.meta
      if (topicName) {
        const topic = state.topics.get(topicName) || new TopicRecord()
        const reportIds = topic.reportIds.concat(action.payload.results.map(r => r.id))
        const resultsCount = action.payload.results_count
        const nextPage = resultsCount > reportIds.size ? page + 1 : -1
        return state
          .setIn(['topics', topicName], topic.merge({reportIds, resultsCount, nextPage, loading: false}))
          .merge({reports: state.reports.merge(reports)})
      }
      return state.merge({reports: state.reports.merge(reports)})
    }
    case UPDATE_REPORT: {
      const {report} = action
      if (!report.id) {
        throw TypeError('no report id given to UPDATE_REPORT')
      }
      return state.merge({reports: state.reports.set(report.id, report)})
    }
    case pending(SAVE_REPORT): {
      const {wasQueued} = action.meta
      const oldReport = state.reports.get(action.meta.report.id)
      if (oldReport.savePending) {
        // save race condition is a hard error because we like to not blow up user data
        throw new Error('race condition detected, two saves pending at the same time')
      }
      const saveQueued = oldReport.saveQueued && !wasQueued
      const report = {...oldReport, savePending: true, saveQueued}
      return state.merge({reports: state.reports.set(report.id, report)})
    }
    // since all this does right now is clear the pending flag, save rejected can share logic with fulfilled
    case rejected(SAVE_REPORT):
    case fulfilled(SAVE_REPORT): {
      const report = {...state.reports.get(action.meta.report.id), savePending: false, updatedAt: new Date().toISOString()}
      return state.merge({reports: state.reports.set(report.id, report)})
    }
    case pending(LIKE_REPORT): {
      const {reportId} = action.meta
      const oldReport = state.reports.get(reportId)
      const report = {...oldReport, likedByCurrentUser: true, likesCount: oldReport.likesCount + 1}
      return state.merge({reports: state.reports.set(report.id, report)})
    }
    case pending(UNLIKE_REPORT): {
      const {reportId} = action.meta
      const oldReport = state.reports.get(reportId)
      const report = {...oldReport, likedByCurrentUser: false, likesCount: oldReport.likesCount - 1}
      return state.merge({reports: state.reports.set(report.id, report)})
    }
    case fulfilled(LIKE_REPORT):
    case fulfilled(UNLIKE_REPORT): {
      const {report} = action.payload
      return state.merge({reports: state.reports.set(report.id, deserializeReport(report))})
    }
    case fulfilled(ASSOCIATE_PHOTO):
    case fulfilled(ADD_PHOTO): {
      const oldReport = state.reports.get(action.meta.reportId)
      const {payload} = action
      const report = {...oldReport, photos: oldReport.photos.concat([deserializePhoto(payload.photo)])}
      return state.merge({reports: state.reports.set(report.id, report)})
    }
    case pending(DELETE_PHOTO):
    case fulfilled(DELETE_PHOTO): {
      const {reportId, photoId} = action.meta
      const oldReport = state.reports.get(reportId)
      const report = {...oldReport,
        photos: oldReport.photos.filter(p => p.id !== photoId),
        featuredImage: oldReport.featuredImage === photoId ? null : oldReport.featuredImage
      }
      return state.merge({reports: state.reports.set(report.id, report)})
    }
    case fulfilled(DELETE_REPORT): {
      const {report} = action.meta
      const reports = state.reports.delete(report.id)
      const topics = state.topics.map(t => t.merge({reportIds: t.reportIds.filter(id => id !== report.id)}))
      return state.merge({reports, topics})
    }
    case pending(RESTORE_REPORT): {
      const {reportId} = action.meta
      const report = state.reports.get(reportId)
      return state.setIn(['reports', reportId], {...report, deletedAt: null})
    }
    case rejected(RESTORE_REPORT):
    case fulfilled(RESTORE_REPORT): {
      const {report} = action.payload
      const deserialized = deserializeReport(report)
      return state.merge({reports: state.reports.set(deserialized.id, deserialized)})
    }
    case QUEUE_SAVE: {
      const report = {...state.reports.get(action.report.id), saveQueued: true}
      return state.merge({reports: state.reports.set(report.id, report)})
    }
    default:
      return state
  }
}

const PER_PAGE = 16

// Action Creators
export const loadReportsForIndex = (endpoint, page = 0) => {
  const query = buildQueryString({limit: PER_PAGE, offset: page * PER_PAGE})
  return {
    type: LOAD_INDEX_TOPIC,
    payload: fetchJSON(`/ride_reports/${endpoint}.json?${query}`),
    meta: {endpoint, page}
  }
}

export const loadUserReports = (userId, ...rest) => loadReportsForIndex(`user/${userId}`, ...rest)

export const loadReport = (reportId) => ({
  type: LOAD_REPORT,
  payload: fetchJSON(`/ride_reports/${reportId}.json`)
})

export const loadReports = (params = {}, {topicName, page = 0} = {}) => {
  return {
    meta: {topicName, page},
    type: LOAD_REPORTS,
    payload: fetchJSON('/ride_reports.json?' + buildQueryString({limit: PER_PAGE, offset: page * PER_PAGE, ...params}))
  }
}

export const loadReportsById = (reportIds) => {
  return {
    meta: {},
    type: LOAD_REPORTS,
    payload: fetchJSON('/ride_reports.json?' + reportIds.map(id => 'id[]=' + id).join('&'))
  }
}

export const newReport = (report) => ({
  type: NEW_REPORT,
  payload: fetchJSON('/ride_reports.json', {json: JSON.stringify(report)}, {method: 'POST'})
})

export const updateReport = (report) => ({
  type: UPDATE_REPORT,
  report
})

// If you're using this instead of requestSave, you'd better have a good reason.
export const saveReport = (report, {wasQueued = false, force = false} = {}) => {
  return {
    type: SAVE_REPORT,
    payload: fetchJSON(
      `/ride_reports/${report.id}.json${force ? '?force=true' : ''}`,
      {json: serializeReport(report)},
      {method: 'PATCH'}
    ),
    meta: {report, wasQueued}
  }
}

export const likeReport = (reportId) => ({
  type: LIKE_REPORT,
  payload: fetchJSON(`/ride_reports/${reportId}/like.json`, {}, {method: 'POST'}),
  meta: {reportId}
})

export const unlikeReport = (reportId) => ({
  type: UNLIKE_REPORT,
  payload: fetchJSON(`/ride_reports/${reportId}/unlike.json`, {}, {method: 'POST'}),
  meta: {reportId}
})

export const addPhoto = (reportId, photo) => {
  const body = new window.FormData()
  body.append('file', photo)
  body.append('parent_type', 'ride_report')
  body.append('parent_id', reportId)
  const payload = fetchJSON('/photos.json', null, {
    method: 'POST',
    body,
    credentials: 'same-origin',
    headers: {Accept: 'application/json'}
  })

  return {
    type: ADD_PHOTO,
    payload,
    meta: {reportId}
  }
}

export const associatePhoto = (reportId, photoId) => ({
  type: ASSOCIATE_PHOTO,
  meta: {reportId, photoId},
  payload: fetchJSON('/photosets/associate_photo.json',
    {
      photo_id: photoId,
      parent_id: reportId,
      parent_type: 'ride_report'
    }
  )
})
// problem: if we associate photos with a report, how do we allow users to
// delete them independently of deleting them from the ride? We need to
// somehow only delete photos that have no other associations...
//
// this info/logic (tracking whether a photo has multiple photoset associations
// and doing destory dependent on that) definitely does NOT belong on the
// client-side...

export const deletePhoto = (reportId, photoId) => ({
  type: DELETE_PHOTO,
  payload: fetchJSON(`/photosets/disassociate_photo.json`,
    {
      photo_id: photoId,
      parent_id: reportId,
      parent_type: 'ride_report'
    }
  ),
  meta: {reportId, photoId}
})

// smart, async version of saveReport that avoids race conditions and will
// minimize network requests.
// TODO this should take a report id, not a full report object.
export const requestSave = (report, {force = false} = {}) => (dispatch, getState) => {
  const canonicalReport = getState().rideReports.reports.get(report.id)
  const {savePending} = canonicalReport
  const checkForQueuedSave = () => {
    const latestReport = getState().rideReports.reports.get(report.id)
    if (latestReport.saveQueued) {
      dispatch(saveReport(latestReport, {wasQueued: true, force})).then(checkForQueuedSave)
    }
  }

  if (!savePending) {
    // if there's no save in progress, dispatch a normal save action
    return dispatch(saveReport(canonicalReport, {force})).then(checkForQueuedSave)
  } else {
    // otherwise, set the queuedSave flag to true
    return dispatch({
      type: QUEUE_SAVE,
      report: canonicalReport
    })
  }
}

export const deleteReport = (report) => ({
  type: DELETE_REPORT,
  payload: fetchJSON(
    `/ride_reports/${report.id}.json`,
    null,
    {method: 'DELETE'}
  ),
  meta: {report}
})

export const restoreReport = (reportId) => ({
  type: RESTORE_REPORT,
  payload: fetchJSON(
    `/ride_reports/${reportId}/undelete.json`,
    null,
    {method: 'POST'}
  ),
  meta: {reportId}
})

// convenient macro to set the featured image on a report
export const setFeaturedImage = (reportId, imageId) => (dispatch, getState) => {
  const report = getState().rideReports.reports.get(reportId)
  const newReport = {...report, featuredImage: imageId}
  dispatch(updateReport(newReport))
  dispatch(requestSave(newReport))
}
